import { useState } from 'react';
import PillarScore from './PillarScore';
import RadarChart from './RadarChart';
import ScoreGauge from './ScoreGauge';
import RevenueLeakMap from './RevenuLeakMap';
import MemoryTest from './MemoryTest';
import WhatIfSimulator from './WhatIfSimulator';
import SocialProofCard from './SocialProofCard';
import BrandCoherenceMap from './BrandCoherenceMap';
import CompetitiveRadar from './CompetitiveRadar';
import TierProgression from './TierProgression';
import SentimentAnalysis from './SentimentAnalysis';
import SeasonalityChart from './SeasonalityChart';
import IndustryBenchmark from './IndustryBenchmark';
import ReAuditComparison from './ReAuditComparison';
import AIResearchButton from './AIResearchButton';
import MenuBeverageTrends from './MenuBeverageTrends';
import ReportComments from './ReportComments';
import ScoreHistory from './ScoreHistory';
import PhotoCapture from './PhotoCapture';
import FileUploadPanel from './FileUploadPanel';
import TeamScoring from './TeamScoring';
import MenuItemScoring from './MenuItemScoring';
import WeightedPriorities from './WeightedPriorities';
import BenchmarkCompare from './BenchmarkCompare';
import ExperienceMapping from './ExperienceMapping';
import RevenueLabor from './RevenueLabor';
import ConceptDrift from './ConceptDrift';
import DigitalFriction from './DigitalFriction';
import GuestJourney from './GuestJourney';
import CompetitiveKillSheet from './CompetitiveKillSheet';
import SignatureMoments from './SignatureMoments';
import RiskRadar from './RiskRadar';
import ContentCalendar from './ContentCalendar';
import ExecEdit from './ExecEdit';
import TrainingCurriculum from './TrainingCurriculum';
import {
  PILLARS, calculateNextIndex, calculateOverallConfidence,
  getConfidenceColor, createDefaultRevenuLeaks,
  createDefaultMemoryTest, createDefaultSocialProof,
  createDefaultBrandCoherence,
} from '../utils/scoring';

const TABS = [
  { id: 'research', label: 'AI Research' },
  { id: 'pillars', label: 'Score' },
  { id: 'journey', label: 'Visit Audit' },
  { id: 'analysis', label: 'Analysis' },
  { id: 'execEdit', label: 'Executive' },
  { id: 'competitive', label: 'Competitive' },
  { id: 'deliverables', label: 'Deliverables' },
  { id: 'training', label: 'Training' },
];

export default function ScoringDashboard({ audit, onUpdate, previousAudit = null }) {
  const [activeTab, setActiveTab] = useState('research');
  const scores = audit?.scores || {};

  const nextIndex = calculateNextIndex(scores);
  const confidence = calculateOverallConfidence(scores);

  // Safe array helper used throughout
  const safeArr = (val) => Array.isArray(val) ? val : [];

  // Extended data (initialize if missing)
  const revenueLeaks = audit.revenueLeaks || createDefaultRevenuLeaks();
  const memoryTest = audit.memoryTest || createDefaultMemoryTest();
  const socialProof = audit.socialProof || createDefaultSocialProof();
  const brandCoherence = audit.brandCoherence || createDefaultBrandCoherence();

  const handleScoreChange = (pillarId, data) => {
    onUpdate({
      ...audit,
      scores: { ...scores, [pillarId]: data },
    });
  };

  const updateExtended = (field, value) => {
    onUpdate({ ...audit, [field]: value });
  };

  return (
    <div>
      {/* Top summary bar */}
      <div className="card p-6 mb-6">
        <div className="flex flex-col lg:flex-row items-center gap-6">
          <ScoreGauge score={nextIndex} />

          <div className="flex-1 text-center lg:text-left">
            <h2 className="text-2xl font-bold text-white mb-1">{audit.intake?.conceptName}</h2>
            <p className="text-sm text-navy-400">{audit.intake?.city}</p>
            <div className="flex items-center gap-3 mt-3 justify-center lg:justify-start">
              <span className={`text-xs px-3 py-1 rounded-full font-medium ${getConfidenceColor(confidence)}`}>
                {confidence} Confidence
              </span>
              <span className="text-xs text-navy-500">
                {PILLARS.filter(p => scores[p.id]?.score > 0).length}/{PILLARS.length} pillars scored
              </span>
            </div>
          </div>

          {/* Quick radar */}
          <div className="w-full lg:w-80">
            <RadarChart scores={scores} />
          </div>
        </div>
      </div>

      {/* Team Scoring + Custom Weighting + File Uploads */}
      <TeamScoring audit={audit} onUpdate={onUpdate} />
      <WeightedPriorities audit={audit} onUpdate={onUpdate} />
      <FileUploadPanel audit={audit} onUpdate={onUpdate} />

      {/* Re-audit comparison banner */}
      {previousAudit && (
        <div className="mb-6">
          <ReAuditComparison currentAudit={audit} previousAudit={previousAudit} />
        </div>
      )}

      {/* Tabs — scrollable on mobile */}
      <div className="mb-6 overflow-x-auto -mx-1 px-1 scrollbar-hide">
        <div className="flex gap-1 bg-neutral-900/60 rounded-xl p-1 border border-neutral-800/50 min-w-max">
          {TABS.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`whitespace-nowrap px-4 sm:px-4 py-3 sm:py-2.5 rounded-lg text-sm sm:text-sm font-medium transition-all active:scale-95 ${
                activeTab === tab.id
                  ? 'bg-brand-gold/15 text-brand-gold shadow-sm'
                  : 'text-neutral-400 hover:text-neutral-200'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Tab: AI Research */}
      {activeTab === 'research' && (
      <div className="space-y-6">
        <AIResearchButton
          intake={audit.intake}
          existingScores={scores}
          marketData={audit.marketData || null}
          approvedCompetitors={audit.approvedCompetitors || null}
          aiResearch={audit.aiResearch || null}
          onCompetitorsFound={(comps) => {}}
          onCompetitorsApproved={(comps) => {
            onUpdate({ ...audit, approvedCompetitors: comps });
          }}
          onMarketComplete={(marketResult) => {
            // Merge market research results back into approved competitors
            const updatedComps = Array.isArray(audit.approvedCompetitors) ? [...audit.approvedCompetitors] : [];
            const marketComps = Array.isArray(marketResult.competitors) ? marketResult.competitors : [];

            updatedComps.forEach((comp, i) => {
              // Find matching market data by name
              const match = marketComps.find(mc =>
                mc.name && comp.name && mc.name.toLowerCase().includes(comp.name.toLowerCase().split(' ')[0])
              );
              if (match) {
                updatedComps[i] = {
                  ...comp,
                  strengths: match.strengths || comp.strengths || '',
                  weaknesses: match.weaknesses || comp.weaknesses || '',
                  reviewScore: match.reviewScore || comp.realGoogleRating || comp.reviewScore || 0,
                  totalReviews: match.totalReviews || comp.realReviewCount || comp.totalReviews || 0,
                  priceRange: match.priceRange || comp.priceRange || '',
                  type: comp.type || match.type || 'Restaurant',
                  estimatedRevenue: match.estimatedRevenue || comp.estimatedRevenue || '',
                  menuPricing: match.menuPricing || comp.menuPricing || null,
                  instagram: match.instagram || comp.instagram || null,
                };
              }
            });

            onUpdate({
              ...audit,
              approvedCompetitors: updatedComps,
              marketData: {
                ...marketResult,
                completedAt: new Date().toISOString(),
              },
            });
          }}
          onAuditComplete={(aiData) => {
            // Map camelCase AI keys to underscore app keys
            const keyMap = {
              conceptClarity: 'concept_clarity',
              brandExpression: 'brand_expression',
              guestSignal: 'guest_signal',
              operationalConfidence: 'operational_confidence',
              demandGeneration: 'demand_generation',
              commercialPerformance: 'commercial_performance',
              growthReadiness: 'growth_readiness',
              emotionalExperience: 'emotional_experience',
              menuIntelligence: 'menu_intelligence',
              beverageIntelligence: 'beverage_intelligence',
            };

            const mergedScores = { ...scores };
            if (aiData.scores) {
              for (const [key, val] of Object.entries(aiData.scores)) {
                const appKey = keyMap[key] || key;
                if (!mergedScores[appKey] || mergedScores[appKey].score === 0) {
                  // Empty or default score — AI fills it completely
                  mergedScores[appKey] = {
                    ...val,
                    aiGenerated: true,
                    aiSuggested: val.score,
                    aiRationale: val.rationale || '',
                    rationale: '',
                  };
                } else {
                  // User already scored — keep their score, enhance the rationale, show AI suggestion
                  const existingRationale = mergedScores[appKey].rationale || '';
                  const aiRationale = val.rationale || '';
                  mergedScores[appKey] = {
                    ...mergedScores[appKey],
                    rationale: existingRationale
                      ? `${existingRationale}\n\n[AI Research]: ${aiRationale}`
                      : aiRationale,
                    aiSuggested: val.score,
                    aiRationale: aiRationale,
                    evidence: val.evidence || mergedScores[appKey].evidence,
                    confidence: val.confidence || mergedScores[appKey].confidence,
                  };
                }
              }
            }
            // Merge AI revenue leaks into the manual leak map
            const mergedLeaks = { ...(audit.revenueLeaks || revenueLeaks) };
            if (aiData.revenueLeaks && Array.isArray(aiData.revenueLeaks)) {
              const leakKeywords = {
                menu_mispricing: ['pric', 'menu pric', 'overpric', 'underpric', 'margin'],
                low_check_avg: ['check', 'average', 'spend', 'ticket'],
                empty_seats: ['seat', 'cover', 'capacity', 'occupancy', 'empty', 'traffic'],
                no_events: ['event', 'programming', 'activation', 'live'],
                no_private_dining: ['private', 'dining', 'buyout', 'group'],
                weak_bev_attachment: ['beverage', 'drink', 'attachment', 'bar', 'cocktail', 'wine'],
                poor_upsell: ['upsell', 'upgrade', 'add-on', 'premium'],
                no_merch_retail: ['merch', 'retail', 'merchandise', 'product'],
              };
              aiData.revenueLeaks.forEach(aiLeak => {
                if (!aiLeak.area) return;
                const areaLower = (aiLeak.area + ' ' + (aiLeak.issue || '')).toLowerCase();
                let matched = false;
                for (const [leakId, keywords] of Object.entries(leakKeywords)) {
                  if (keywords.some(k => areaLower.includes(k))) {
                    mergedLeaks[leakId] = {
                      active: true,
                      estimatedImpact: mergedLeaks[leakId]?.estimatedImpact || 15000,
                      notes: `${aiLeak.area}: ${aiLeak.issue || ''}${aiLeak.fix ? ' — Fix: ' + aiLeak.fix : ''}`,
                    };
                    matched = true;
                    break;
                  }
                }
                if (!matched) {
                  // Add as custom leak
                  const customId = 'ai_' + aiLeak.area.toLowerCase().replace(/\s+/g, '_').substring(0, 30);
                  mergedLeaks[customId] = {
                    active: true,
                    estimatedImpact: aiLeak.impact === 'High' ? 30000 : aiLeak.impact === 'Medium' ? 15000 : 8000,
                    notes: `${aiLeak.issue || ''}${aiLeak.fix ? ' — Fix: ' + aiLeak.fix : ''}`,
                    customName: aiLeak.area,
                    customIcon: '🔍',
                  };
                }
              });
            }

            // Auto-populate social proof from AI data
            const mergedSocialProof = { ...(audit.socialProof || socialProof) };
            if (aiData.socialProof) {
              const sp = aiData.socialProof;
              // Map AI social proof metrics to slider scores (1-10 scale)
              if (sp.followers) {
                // Score based on follower count (hospitality scale)
                const f = typeof sp.followers === 'number' ? sp.followers : parseInt(sp.followers) || 0;
                mergedSocialProof.follower_reach = f > 50000 ? 9 : f > 20000 ? 8 : f > 10000 ? 7 : f > 5000 ? 6 : f > 2000 ? 5 : f > 1000 ? 4 : 3;
              }
              if (sp.engagementRate) {
                const rate = parseFloat(sp.engagementRate) || 0;
                mergedSocialProof.engagement = rate > 5 ? 9 : rate > 3 ? 8 : rate > 2 ? 7 : rate > 1 ? 6 : rate > 0.5 ? 5 : 4;
              }
              if (sp.postingFrequency) {
                const freq = sp.postingFrequency.toLowerCase();
                mergedSocialProof.consistency = freq.includes('daily') ? 9 : freq.includes('week') ? 7 : freq.includes('month') ? 5 : 4;
              }
              if (sp.contentQuality) {
                const qual = sp.contentQuality.toLowerCase();
                mergedSocialProof.content_quality = qual.includes('high') || qual.includes('excellent') || qual.includes('strong') ? 8 : qual.includes('good') || qual.includes('solid') ? 7 : qual.includes('average') ? 5 : 4;
              }
              if (sp.presenceScore && typeof sp.presenceScore === 'number') {
                mergedSocialProof.hashtag_reach = sp.presenceScore;
              }
              // Direct slider scores from AI (1-10)
              if (sp.posting_consistency) mergedSocialProof.posting_consistency = sp.posting_consistency;
              if (sp.engagement_rate) mergedSocialProof.engagement_rate = sp.engagement_rate;
              if (sp.content_quality) mergedSocialProof.content_quality = sp.content_quality;
              if (sp.guest_tagging) mergedSocialProof.guest_tagging = sp.guest_tagging;
              if (sp.hashtag_performance) mergedSocialProof.hashtag_performance = sp.hashtag_performance;
              mergedSocialProof.notes = sp.notes || mergedSocialProof.notes || '';
              mergedSocialProof.followers = sp.followers;
              mergedSocialProof.engagementRate = sp.engagementRate;
              mergedSocialProof.postingFrequency = sp.postingFrequency;
              mergedSocialProof.contentQuality = sp.contentQuality;
            }

            onUpdate({
              ...audit,
              scores: mergedScores,
              revenueLeaks: mergedLeaks,
              socialProof: mergedSocialProof,
              // Merge AI memory test into audit
              memoryTest: aiData.memoryTest ? {
                ...audit.memoryTest,
                score: aiData.memoryTest.score || audit.memoryTest?.score || 0,
                signatureMoments: (aiData.memoryTest.signatureMoments || []).filter(Boolean).length > 0
                  ? aiData.memoryTest.signatureMoments : (audit.memoryTest?.signatureMoments || []),
                gaps: (aiData.memoryTest.gaps || []).filter(Boolean).length > 0
                  ? aiData.memoryTest.gaps : (audit.memoryTest?.gaps || []),
              } : audit.memoryTest,
              // Merge AI brand coherence
              brandCoherence: aiData.brandCoherence ? {
                ...audit.brandCoherence,
                websiteAlign: aiData.brandCoherence.websiteAlign || audit.brandCoherence?.websiteAlign || '',
                menuAlign: aiData.brandCoherence.menuAlign || audit.brandCoherence?.menuAlign || '',
                socialAlign: aiData.brandCoherence.socialAlign || audit.brandCoherence?.socialAlign || '',
                reviewAlign: aiData.brandCoherence.reviewAlign || audit.brandCoherence?.reviewAlign || '',
                overallNotes: aiData.brandCoherence.overallNotes || audit.brandCoherence?.overallNotes || '',
                _grid: aiData.brandCoherence._grid || audit.brandCoherence?._grid || {},
              } : audit.brandCoherence,
              // Merge AI menu/beverage analysis
              menuAnalysis: aiData.menuAnalysis || audit.menuAnalysis,
              beverageAnalysis: aiData.beverageAnalysis || audit.beverageAnalysis,
              // Auto-fill guest journey from AI data — targets active visit
              guestJourney: (() => {
                const activeIdx = audit._activeVisitIndex || 0;
                const visits = Array.isArray(audit.guestJourneys) && audit.guestJourneys.length > 0 ? audit.guestJourneys : null;
                const gj = { ...(visits ? (visits[activeIdx]?.data || {}) : (audit.guestJourney || {})) };
                const gs = aiData.guestSentiment?.google || aiData.guestSentiment?.combined || aiData.guestSentiment || {};
                const bc = aiData.brandCoherence || {};
                const sp = aiData.socialProof || {};
                const opScore = aiData.scores?.operationalConfidence?.score || 0;
                const emotScore = aiData.scores?.emotionalExperience?.score || 0;
                const menuScore = aiData.scores?.menuIntelligence?.score || 0;
                const bevScore = aiData.scores?.beverageIntelligence?.score || 0;
                const guestScore = aiData.scores?.guestSignal?.score || 0;
                const demandScore = aiData.scores?.demandGeneration?.score || 0;
                const brandScore = aiData.scores?.brandExpression?.score || 0;

                // Helper to check if reviews mention a keyword
                const posThemes = [...(gs.positiveThemes || [])].join(' ').toLowerCase();
                const negThemes = [...(gs.negativeThemes || [])].join(' ').toLowerCase();
                const hasPositive = (keywords) => keywords.some(k => posThemes.includes(k));
                const hasNegative = (keywords) => keywords.some(k => negThemes.includes(k));
                const inferScore = (base, posKeys, negKeys) => {
                  let s = base;
                  if (hasPositive(posKeys)) s = Math.min(10, s + 1.5);
                  if (hasNegative(negKeys)) s = Math.max(1, s - 1.5);
                  return Math.round(s * 10) / 10;
                };

                const aiMappings = {
                  // Pre-Visit
                  awareness: { score: demandScore, research: `Demand Generation: ${demandScore}` },
                  googleSearch: { score: demandScore ? Math.min(10, demandScore * 1.1) : 0, research: `SEO/visibility score: ${demandScore}` },
                  websiteVisit: { score: brandScore || 0, research: bc.websiteAlign || `Brand Expression: ${brandScore}` },
                  socialScroll: { score: sp.presenceScore || demandScore || 0, research: sp.notes || `Social presence: ${sp.followers || 'unknown'} followers` },
                  reviewRead: { score: gs.overallRating ? gs.overallRating * 2 : guestScore, research: `${gs.overallRating || 'N/A'}/5 (${gs.totalReviews || 0} reviews)` },
                  reservation: { score: inferScore(6, ['easy book', 'resy', 'opentable', 'reservation'], ['hard to book', 'no reservation', 'wait list']), research: 'Inferred from review mentions of booking' },
                  // Arrival
                  neighborhood: { score: inferScore(emotScore || 6, ['location', 'neighborhood', 'convenient', 'beautiful area'], ['sketchy', 'hard to find', 'parking']), research: 'Inferred from reviews mentioning location' },
                  exterior: { score: brandScore || 0, research: `Brand expression: ${brandScore}` },
                  entrance: { score: emotScore || 0, research: `Emotional Experience: ${emotScore}` },
                  hostGreeting: { score: inferScore(opScore || 6, ['greeted', 'friendly host', 'warm welcome', 'seated quickly'], ['ignored', 'no greeting', 'rude host', 'long wait']), research: 'Inferred from reviews mentioning greeting/host' },
                  waitExperience: { score: inferScore(6, ['no wait', 'seated immediately'], ['long wait', 'waited forever', '30 min wait', 'hour wait']), research: 'Inferred from reviews mentioning wait times' },
                  seatingWalk: { score: emotScore || 0, research: 'Inferred from emotional experience score' },
                  // The Table
                  tableFirst: { score: inferScore(emotScore || 6, ['cozy', 'comfortable', 'great table', 'nice seating'], ['cramped', 'uncomfortable', 'noisy', 'cold']), research: 'Inferred from reviews mentioning seating/comfort' },
                  ambiance: { score: inferScore(emotScore || 6, ['ambiance', 'atmosphere', 'beautiful', 'romantic', 'vibe', 'music'], ['loud', 'noisy', 'dark', 'bright', 'sterile']), research: 'Inferred from reviews mentioning ambiance' },
                  serverIntro: { score: inferScore(opScore || 6, ['server', 'knowledgeable', 'attentive', 'helpful staff', 'great server'], ['server', 'slow server', 'forgot', 'rude server']), research: 'Inferred from reviews mentioning server' },
                  menuPresentation: { score: menuScore || 0, research: `Menu Intelligence: ${menuScore}` },
                  // Food & Drink
                  firstDrink: { score: inferScore(bevScore || 6, ['cocktail', 'drink', 'amazing drinks', 'creative cocktails'], ['weak drink', 'overpriced drinks', 'slow bar']), research: 'Inferred from beverage reviews' },
                  appetizerCourse: { score: inferScore(menuScore || 6, ['appetizer', 'starter', 'amazing food'], ['cold food', 'bland']), research: 'Inferred from food reviews' },
                  entreeCourse: { score: inferScore(menuScore || 6, ['entree', 'main course', 'delicious', 'perfectly cooked'], ['undercooked', 'overcooked', 'cold', 'bland', 'disappointing']), research: 'Inferred from food reviews' },
                  beveragePairing: { score: bevScore || 0, research: `Beverage Intelligence: ${bevScore}` },
                  pacing: { score: inferScore(opScore || 6, ['perfect pace', 'well timed'], ['slow', 'rushed', 'too fast', 'too slow', 'waited']), research: 'Inferred from reviews mentioning pacing' },
                  dessertOffer: { score: inferScore(menuScore || 5, ['dessert', 'amazing dessert'], ['no dessert', 'skipped dessert']), research: 'Inferred from dessert mentions' },
                  // Service
                  attentiveness: { score: inferScore(opScore || 6, ['attentive', 'anticipat', 'proactive', 'checked on us'], ['neglected', 'invisible', 'had to flag']), research: 'Inferred from service reviews' },
                  checkIns: { score: opScore || 0, research: `Operational Confidence: ${opScore}` },
                  restroomVisit: { score: inferScore(6, ['clean restroom', 'nice bathroom'], ['dirty restroom', 'gross bathroom', 'no soap']), research: 'Inferred from restroom mentions' },
                  // Departure
                  checkPresentation: { score: inferScore(opScore || 6, ['easy to pay', 'quick check'], ['slow check', 'bill was wrong', 'hard to pay']), research: 'Inferred from payment reviews' },
                  farewell: { score: inferScore(opScore || 6, ['thank', 'goodbye', 'see you'], ['rushed out', 'ignored']), research: 'Inferred from departure mentions' },
                  lastImpression: { score: guestScore || 0, research: `Guest Signal: ${guestScore}` },
                  // Post-Visit
                  memoryAt48hrs: { score: emotScore || 0, research: `Emotional Experience: ${emotScore}` },
                  wouldReturn: { score: inferScore(guestScore || 6, ['return', 'come back', 'again', 'regular', 'go back'], ['never again', 'last time', 'won\'t return']), research: 'Inferred from reviews mentioning return intent' },
                  wouldRecommend: { score: inferScore(guestScore || 6, ['recommend', 'must try', 'tell everyone', 'bring friends'], ['wouldn\'t recommend', 'don\'t bother']), research: 'Inferred from reviews mentioning recommendations' },
                };

                Object.entries(aiMappings).forEach(([tp, data]) => {
                  if (data.score > 0) {
                    const rounded = Math.round(data.score * 10) / 10;
                    const alreadyScored = gj[tp]?.scored === true || (gj[tp]?.score || 0) > 0;
                    gj[tp] = {
                      ...gj[tp],
                      score: alreadyScored ? gj[tp].score : rounded,
                      scored: alreadyScored ? gj[tp].scored : true,
                      aiScore: rounded,
                      aiResearch: data.research + (gj[tp]?.aiResearch ? ` | ${gj[tp].aiResearch}` : ''),
                    };
                  }
                });
                return gj;
              })(),
              aiResearch: {
                executiveDiagnostic: aiData.executiveDiagnostic,
                businessInOnePage: aiData.businessInOnePage,
                scoreTensions: aiData.scoreTensions,
                memoryTest: aiData.memoryTest,
                revenueLeaks: aiData.revenueLeaks,
                competitorAnalysis: aiData.competitorAnalysis,
                menuAnalysis: aiData.menuAnalysis,
                beverageAnalysis: aiData.beverageAnalysis,
                guestSentiment: aiData.guestSentiment,
                socialProof: aiData.socialProof,
                instagramData: aiData.socialProof, // Store raw for reference
                brandCoherence: aiData.brandCoherence,
                seasonality: aiData.seasonality,
                roadmap: aiData.roadmap,
                whatSuccessLooksLike: aiData.whatSuccessLooksLike,
                overallConfidence: aiData.overallConfidence,
                completedAt: new Date().toISOString(),
              },
              // Auto-fill digital friction from AI data
              digitalFriction: (() => {
                const df = { ...(audit.digitalFriction || {}) };
                const bc = aiData.brandCoherence || {};
                const sp = aiData.socialProof || {};
                const demandScore = aiData.scores?.demandGeneration?.score || 0;
                const brandScore = aiData.scores?.brandExpression?.score || 0;
                const aiDF = {
                  websiteSpeed: { score: brandScore > 0 ? Math.min(10, brandScore * 1.1) : 0 },
                  mobileResponsive: { score: brandScore > 0 ? Math.min(10, brandScore) : 0 },
                  menuAccessible: { score: bc.menuAlign ? 7 : 0 },
                  hoursVisible: { score: demandScore > 0 ? Math.min(10, demandScore * 1.1) : 0 },
                  photoQuality: { score: brandScore > 0 ? Math.min(10, brandScore * 1.05) : 0 },
                  googleProfile: { score: aiData.guestSentiment?.google?.overallRating ? aiData.guestSentiment.google.overallRating * 2 : 0 },
                  seoVisibility: { score: demandScore },
                  contentFreshness: { score: sp.postingFrequency ? 7 : (demandScore > 0 ? 5 : 0) },
                  instaLink: { score: sp.followers ? 8 : (demandScore > 0 ? 5 : 0) },
                };
                Object.entries(aiDF).forEach(([key, val]) => {
                  if ((!df[key]?.score || df[key].score === 0) && val.score > 0) {
                    df[key] = { ...df[key], score: Math.round(val.score * 10) / 10 };
                  }
                });
                return df;
              })(),
            });
          }}
        />
      </div>
      )}

      {/* Tab: Score (Pillars) */}
      {activeTab === 'pillars' && (
        <div>
          <div className="grid md:grid-cols-2 gap-4 mb-6">
            {PILLARS.map((pillar, i) => (
              <div key={pillar.id} style={{ animationDelay: `${i * 50}ms` }}>
                <PillarScore
                  pillar={pillar}
                  data={scores[pillar.id] || { score: 5, rationale: '', confidence: 'Medium', evidence: 'Inferred' }}
                  onChange={handleScoreChange}
                />
              </div>
            ))}
          </div>

          {/* Tier progression */}
          <TierProgression currentScore={nextIndex} scores={scores} />
        </div>
      )}

      {/* Tab: Executive */}
      {activeTab === 'execEdit' && (
        <div className="space-y-6">
          <ExecEdit audit={audit} onUpdate={onUpdate} />
        </div>
      )}

      {/* Tab: Visit Audit */}
      {activeTab === 'journey' && (
        <div className="space-y-6">
          <GuestJourney audit={audit} onUpdate={onUpdate} />
        </div>
      )}

      {/* Tab: Analysis — Brand, Menu, Sentiment, Social, Memory, Digital, Concept, Revenue */}
      {activeTab === 'analysis' && (
        <div className="space-y-6">
          <MenuBeverageTrends
            data={{
              menuAnalysis: audit.menuAnalysis || audit.aiResearch?.menuAnalysis || {},
              beverageAnalysis: audit.beverageAnalysis || audit.aiResearch?.beverageAnalysis || {},
              menuTrends: audit.marketData?.menuTrends || {},
            }}
            onChange={(val) => {
              onUpdate({ ...audit, menuAnalysis: val.menuAnalysis, beverageAnalysis: val.beverageAnalysis });
            }}
          />
          <BrandCoherenceMap
            data={audit.brandCoherence || brandCoherence}
            onChange={(val) => updateExtended('brandCoherence', val)}
          />
          {audit.aiResearch && (
            <>
              <SentimentAnalysis data={audit.aiResearch.guestSentiment} />
              <SeasonalityChart data={audit.aiResearch.seasonality} />
            </>
          )}
          <SocialProofCard
            data={audit.socialProof || socialProof}
            onChange={(val) => updateExtended('socialProof', val)}
          />
          <MemoryTest
            data={audit.memoryTest || memoryTest}
            onChange={(val) => updateExtended('memoryTest', val)}
          />
          <DigitalFriction audit={audit} onUpdate={onUpdate} />
          <ConceptDrift audit={audit} />
          <RevenueLeakMap
            leaks={revenueLeaks}
            onChange={(val) => updateExtended('revenueLeaks', val)}
          />
          <RiskRadar audit={audit} />
          {previousAudit && <ScoreHistory audits={[previousAudit, audit]} />}
        </div>
      )}

      {/* Tab: Competitive */}
      {activeTab === 'competitive' && (
        <div className="space-y-6">
          {audit.marketData ? (
            <>
              <CompetitiveRadar scores={scores} marketData={audit.marketData} competitors={Array.isArray(audit.approvedCompetitors) ? audit.approvedCompetitors : Array.isArray(audit.marketData?.competitors) ? audit.marketData.competitors : []} />
              <IndustryBenchmark scores={scores} marketData={audit.marketData} competitors={Array.isArray(audit.approvedCompetitors) ? audit.approvedCompetitors : Array.isArray(audit.marketData?.competitors) ? audit.marketData.competitors : []} />
            </>
          ) : (
            <div className="card p-8 text-center">
              <h3 className="font-display text-base text-neutral-400 mb-1">Competitive Intelligence</h3>
              <p className="text-neutral-600 text-xs">Run AI Research first to unlock competitive data</p>
            </div>
          )}
          <CompetitiveKillSheet audit={audit} />
          <BenchmarkCompare audit={audit} />
        </div>
      )}

      {/* Tab: Deliverables — Ideation, What-If, Photos, Comments */}
      {activeTab === 'deliverables' && (
        <div className="space-y-6">
          <SignatureMoments audit={audit} onUpdate={onUpdate} />
          <ContentCalendar audit={audit} onUpdate={onUpdate} />
          <WhatIfSimulator scores={scores} currentIndex={nextIndex} />
          <MenuItemScoring audit={audit} onUpdate={onUpdate} />
          <PhotoCapture
            photos={audit.photos || []}
            onChange={(photos) => onUpdate({ ...audit, photos })}
          />
          <ReportComments audit={audit} onUpdate={onUpdate} />
        </div>
      )}

      {/* Tab: Training Curriculum */}
      {activeTab === 'training' && (
        <div className="space-y-6">
          <TrainingCurriculum audit={audit} onUpdate={onUpdate} />
        </div>
      )}
    </div>
  );
}
